// src/app/router.tsx
import React from 'react';
import {
    createBrowserRouter,
    type RouteObject,
    useLocation,
    useRouteError,
    isRouteErrorResponse,
    Link,
} from 'react-router-dom';

import { App } from '@/App';
import { Layout } from '@/layout/Layout';
import { Home, MemberPage, VideoIdRedirect, VideoListPage, VideoPage, InterviewsListPage } from '@/pages';
import { AnchorRedirect } from '@/app/AnchorRedirect';
import { ErrorBoundary } from '@/app/ErrorBoundary';

// Layout + ErrorBoundary, БЕЗ children для Layout
function RootWithBoundary() {
    const { pathname, key } = useLocation();
    return (
        <ErrorBoundary resetKeys={[key, pathname]}>
            <Layout />
        </ErrorBoundary>
    );
}

function RouteError() {
    const err = useRouteError() as any;
    if (isRouteErrorResponse(err)) {
        return (
            <div style={{ padding: 24 }}>
                <h1>
                    {err.status} {err.statusText}
                </h1>
                <p>{err.data?.message ?? 'Page error'}</p>
                <p><Link to="/">Go home</Link></p>
            </div>
        );
    }
    console.error('Route error:', err);
    return (
        <div style={{ padding: 24 }}>
            <h1>Something went wrong</h1>
            <p>{(err as Error)?.message ?? String(err)}</p>
            <p><Link to="/">Go home</Link></p>
        </div>
    );
}

const routes: RouteObject[] = [
    {
        path: '/',
        element: <App />,
        errorElement: <RouteError />,
        children: [
            {
                element: <RootWithBoundary />, // вместо <LayoutWithBoundary />
                children: [
                    { index: true, element: <Home /> },
                    { path: 'about-us', element: <AnchorRedirect hash="#about" /> },
                    {
                        path: 'videos',
                        children: [
                            { index: true, element: <VideoListPage /> },
                            { path: ':id(\\d+)', element: <VideoIdRedirect /> },
                            { path: ':slug', element: <VideoPage /> },
                        ],
                    },
                    {
                        path: 'interviews',
                        children: [
                            { index: true, element: <InterviewsListPage /> },
                            { path: ':id(\\d+)', element: <VideoIdRedirect /> },
                            { path: ':slug', element: <VideoPage /> },
                        ],
                    },
                    {
                        path: 'meet-the-pros',
                        children: [
                            { index: true, element: <AnchorRedirect hash="#meet-the-pros" /> },
                            { path: ':pathname', element: <MemberPage /> },
                        ],
                    },
                    { path: '*', element: <RouteError /> },
                ],
            },
        ],
    },
];

export const router = createBrowserRouter(routes);
